import { useCallback, useEffect, useMemo, useState } from 'react';

import FeatureGates from '@atlaskit/feature-gate-js-client';
import type { NudgeRecommendationDetails } from '@atlassian/convo-ai-api';

import { fetchNudgeRecommendations } from './fetchNudgeRecommendations';

const DEFAULT_NUDGE_RECOMMENDATION_CACHE_TTL_MS = 10 * 60 * 1000;

const getNudgeRecommendationCacheTTL = (): number =>
	FeatureGates.getExperimentValue<number>(
		'page_catchup_cache_ttl',
		'value',
		DEFAULT_NUDGE_RECOMMENDATION_CACHE_TTL_MS,
	);

type useFetchNudgeRecommendationsReturnType = {
	error?: Error;
	loading: boolean;
	recommendations: NudgeRecommendationDetails[] | undefined;
};

type PreviousRequestCache = {
	cachedAt?: number;
	pageUrl?: string;
};

const isPageCatchupCacheTTLExperimentEnabled = (hasPageCatchupNudge: boolean) => {
	if (!hasPageCatchupNudge) {
		return false;
	}
	return FeatureGates.getExperimentValue(
		'add_cache_ttl_for_page_catchup_nudge_experiment',
		'isEnabled',
		false,
	);
};

export const useFetchNudgeRecommendations = (
	triggerId: string,
	shouldTrigger: boolean,
	pageUrl: string,
	product: string,
	hasPageCatchupNudge = false,
): useFetchNudgeRecommendationsReturnType => {
	const [error, setError] = useState<Error | undefined>();
	const [loading, setLoading] = useState<boolean>(true);
	const [recommendations, setRecommendations] = useState<NudgeRecommendationDetails[] | undefined>(
		undefined,
	);
	const [previousRequestCache, setPreviousRequestCache] = useState<PreviousRequestCache>({});

	const makeNudgeRecommendationsRequest = useCallback(async () => {
		const isSamePageRequest = pageUrl === previousRequestCache.pageUrl;
		const isDuplicateRequestWithinCacheTTL =
			isSamePageRequest &&
			typeof previousRequestCache.cachedAt === 'number' &&
			Date.now() - previousRequestCache.cachedAt < getNudgeRecommendationCacheTTL();
		const shouldSkipDuplicatePageRequest = isPageCatchupCacheTTLExperimentEnabled(
			hasPageCatchupNudge,
		)
			? isDuplicateRequestWithinCacheTTL
			: isSamePageRequest;

		if (product !== 'confluence' || pageUrl.includes('/wiki/search')) {
			// Temporary: only Confluence is migrated to new nudging system
			// Temporary: added search to skip nudge BE
			setLoading(false);
			return;
		}

		if (
			!product || // invalid request parameter
			!shouldTrigger || // unsupported, no nudges to recommend
			shouldSkipDuplicatePageRequest // prevent duplicate page requests
		) {
			setLoading(false);
			return;
		}

		// Begin new request under valid conditions
		if (!loading && product && shouldTrigger && !shouldSkipDuplicatePageRequest) {
			setLoading(true);
			setError(undefined);
			setRecommendations(undefined);
			return;
		}

		try {
			const response = await fetchNudgeRecommendations({ triggerId, pageUrl, product });
			setRecommendations(
				response?.triggers.flatMap((trigger) =>
					trigger.recommendations.map((recommendation) => recommendation.recommendation_details),
				),
			);
			setError(undefined);
		} catch (error: unknown) {
			setRecommendations(undefined);
			setError(error as Error);
		} finally {
			setPreviousRequestCache({
				pageUrl,
				cachedAt: Date.now(),
			});
			setLoading(false);
		}
	}, [
		loading,
		pageUrl,
		previousRequestCache,
		product,
		shouldTrigger,
		triggerId,
		hasPageCatchupNudge,
	]);

	useEffect(() => {
		void makeNudgeRecommendationsRequest();
	}, [loading, makeNudgeRecommendationsRequest]);

	return useMemo(
		() => ({
			recommendations: loading ? undefined : recommendations,
			loading,
			error,
		}),
		[loading, error, recommendations],
	);
};
